<template>
  <teleport to="body">
    <div
      v-if="isProjectPage && !user.profile.showProjectOnboarding"
      class="fixed top-0 left-0 w-full h-full z-50 "
      :class="backdrop"
    >
      <div
        ref="wrapper"
        class="absolute z-100 shadow px-20 py-15 text-14 rounded-10 text-app-color transition-left-300"
        :class="[
          projectInstructionClasses,
          { 'left-1/2 transform -translate-x-1/2': isMobile },
          { 'top-1/2 transform -translate-y-1/2': top === 0 }
        ]"
        :style="projectInstructionsStyles"
      >
        <div class="flex items-center">
          <p class="text-16 font-semibold mr-10">{{ tooltip.title }}</p>
          <i v-if="activeStep !== 4" class="text-21" :class="tooltip.iconName" />
          <svg v-else class="w-15 h-15 stroke-current text-app-color" xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28" fill="none" stroke="none">
            <path d="M11.1463 15.584H2.26829C1.56783 15.584 1 16.1518 1 16.8523V25.7303C1 26.4308 1.56783 26.9986 2.26829 26.9986H11.1463C11.8468 26.9986 12.4146 26.4308 12.4146 25.7303V16.8523C12.4146 16.1518 11.8468 15.584 11.1463 15.584Z" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M25.7303 15.584H16.8523C16.1518 15.584 15.584 16.1518 15.584 16.8523V25.7303C15.584 26.4308 16.1518 26.9986 16.8523 26.9986H25.7303C26.4308 26.9986 26.9986 26.4308 26.9986 25.7303V16.8523C26.9986 16.1518 26.4308 15.584 25.7303 15.584Z" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M25.7303 1H16.8523C16.1518 1 15.584 1.56783 15.584 2.26829V11.1463C15.584 11.8468 16.1518 12.4146 16.8523 12.4146H25.7303C26.4308 12.4146 26.9986 11.8468 26.9986 11.1463V2.26829C26.9986 1.56783 26.4308 1 25.7303 1Z" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M11.1463 1H2.26829C1.56783 1 1 1.56783 1 2.26829V11.1463C1 11.8468 1.56783 12.4146 2.26829 12.4146H11.1463C11.8468 12.4146 12.4146 11.8468 12.4146 11.1463V2.26829C12.4146 1.56783 11.8468 1 11.1463 1Z" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </div>
        <!-- eslint-disable-next-line vue/no-v-html -->
        <div class="mt-4" v-html="tooltip.description" />

        <!-- M O D A L  F O O T E R -->
        <div class="flex items-center justify-between mt-10" @click.stop.prevent>
          <div class="w-1/3">
            <!-- S K I P  I N S T R U C T I O N S  B U T T O N -->
            <ButtonModule
              v-if="activeStep !== 4"
              text="skip"
              class="text-link-color font-semibold"
              @click="acceptProjectInstruction"
            />
          </div>

          <!-- M O D A L  S T E P S -->
          <div class="flex w-1/3 justify-center">
            <span v-for="step in 4" :key="step" class="rounded-full w-8 h-8 block mr-7" :class="step === activeStep ? 'bg-grey-main' : 'bg-grey-800'" />
          </div>

          <ButtonModule
            :text="activeStep === 4 ? 'Got it' : 'Next'"
            class="w-1/3 py-10 flex justify-center rounded-4 bg-link-color hover:bg-transparent text-white hover:text-link-color border-2 border-link-color text-15"
            @click.prevent="next"
          />
        </div>
      </div>
    </div>
  </teleport>
</template>

<script>
import ButtonModule from '@/components/shared/form-controls/ButtonModule'
import localStorageService from '@/core/local-storage'
import { mapState, mapGetters, mapMutations, mapActions } from 'vuex'
import { profileService } from '@/services'
import { preventBodyScroll } from '@/core/helper-functions'

export default {
  components: { ButtonModule },

  data () {
    return {
      accept: localStorageService.getItem('acceptInstruction') || {
        books: false,
        project: false
      },

      timer: null,
      left: -400,
      top: -400
    }
  },

  computed: {
    ...mapState({
      innerWidth: state => state.general.innerWidth,
      user: state => state.profile.user,
      activeStep: state => state.profile.activeStep
    }),
    ...mapGetters(['isDarkMode', 'isMobile', 'isTeamAccount']),

    backdrop () {
      return this.activeStep === 2 ? '' : 'bg-onboarding-backdrop'
    },

    isProjectPage () {
      return this.$route.name === 'Book' || this.$route.name === 'WeekPlanner' || this.$route.name === 'TeamBook' || this.$route.name === 'Headquarters'
    },

    projectInstructionsStyles () {
      let styles
      if (this.isMobile) {
        styles = this.top !== 0 ? `top: ${this.top}px` : ''
      }
      if (!this.isMobile) {
        styles = `left: ${this.left}px; top: ${this.top}px`
      }
      return styles
    },

    tooltip () {
      const data = { title: '', iconName: '', description: '' }
      switch (this.activeStep) {
        case 1:
          data.title = 'Your pages'
          data.iconName = 'icon-onboarding-file-text'
          data.description = this.isTeamAccount
            ? 'A space consists of pages. We\'ve created an example page for you to play around with.'
            : '<p>We\'ve created some example pages for you to have a look at. All the pages located in the space can be found in the sidebar.</p>'
          break
        case 2:
          data.title = 'The editor'
          data.iconName = 'icon-onboarding-edit'
          data.description = this.isTeamAccount
            ? 'This is where the magic happens. Manage your projects, create documents, add templates, communicate and collaborate.'
            : '<p>This is where the magic happens. Take notes, create lists, write (distraction-free) or start planning. It\'s up to you.</p>'
          break
        case 3:
          data.title = this.isTeamAccount ? 'Chat, share and more' : 'Sharing & other options'
          data.iconName = 'icon-onboarding-share'
          data.description = this.isTeamAccount
            ? 'Chat with your team or share your pages with team members/clients and collaborate together in real-time.'
            : '<p>Easily share your pages with other people and collaborate together in real-time. Use the <span class="italic">"more" </span>menu to change the design, export your information and much more..</p>'
          break
        case 4:
          data.title = 'To your spaces'
          data.iconName = 'icon-onboarding-share'
          data.description = 'Click on the logo to go back to the spaces page.'
          break
      }
      return data
    },

    projectInstructionClasses () {
      if (!this.isMobile) {
        const arrowPosition = this.isDarkMode
          ? `dark-step-${this.activeStep}`
          : `origin-step-${this.activeStep}`

        return `${this.isDarkMode ? 'w-270 bg-sidebar' : 'w-270 bg-white'} ${arrowPosition}`
      }

      return `${this.isDarkMode ? 'w-270 bg-sidebar' : 'w-270 bg-white'}`
    }
  },

  watch: {
    innerWidth () {
      if (this.isProjectPage && !this.user.profile.showProjectOnboarding) {
        this.position()
      }
    },

    activeStep: {
      immediate: true,
      handler (newVal) {
        if (newVal === 1 && this.isMobile && !this.user?.profile?.showProjectOnboarding) {
          this.updateSidebar(true)
        }
        if (newVal === 2 && this.isMobile) {
          this.updateSidebar(false)
        }
      }
    }
  },

  created () {
    this.$nextTick(() => {
      if (this.isProjectPage && !this.user.profile.showProjectOnboarding) {
        this.position()
      }
    })
  },

  methods: {
    ...mapMutations(['setActiveStepProjectInstructions', 'setProjectInstructions', 'updateSidebar', 'loading', 'setNotification']),

    ...mapActions(['getProfile']),

    position () {
      clearTimeout(this.timer)

      this.timer = setTimeout(() => {
        const parrentRect = this.$el.parentElement.getBoundingClientRect()
        const wrapperRect = this.$refs.wrapper.getBoundingClientRect()
        const projectsRect = document.getElementById('projects').getBoundingClientRect()

        if (this.isMobile) {
          if (this.activeStep === 1) {
            const bodyHeight = document.body.offsetHeight
            this.top = (projectsRect.top + projectsRect.height + wrapperRect.height + 20) >= bodyHeight ? 0 : projectsRect.top + projectsRect.height + 20
          } else {
            this.top = 100
          }
        } else {
          if (this.activeStep === 1) {
            this.top = projectsRect.top + (projectsRect.height / 2) - (wrapperRect.height / 2)
            this.left = 305
          } else if (this.activeStep === 2) {
            this.left = parrentRect.left - wrapperRect.width - 40
            this.top = 275
          } else if (this.activeStep === 3) {
            const shareMenu = document.getElementById('share-menu').getBoundingClientRect()
            this.left = shareMenu.left - wrapperRect.width - 37
            this.top = shareMenu.top - 18
          } else if (this.activeStep === 4) {
            const shareMenu = document.getElementById('share-menu').getBoundingClientRect()
            this.top = shareMenu.top - 18
            this.left = 220
          }
        }
      }, 200)
    },

    next () {
      this.$nextTick(() => {
        if (this.activeStep === 4) return this.acceptProjectInstruction()
        this.setActiveStepProjectInstructions(this.activeStep + 1)
        this.position()
      })
    },

    async acceptProjectInstruction () {
      try {
        this.loading(true)
        const payload = {
          email: this.user.email,
          firstName: this.user.profile.firstName,
          lastName: this.user.profile.lastName,
          showProjectOnboarding: true
        }
        await profileService.update(payload)
        await this.getProfile()
        preventBodyScroll(false)
      } catch ({ data }) {
        this.setNotification({
          isVisible: true,
          message: data.error.message,
          color: 'error'
        })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
  .origin-step-1 {
    @extend %instruction-origin-theme-arrow-left;
    &:before {
      @apply top-1/2 transform -translate-y-1/2;
      left: -39px;
    }
  }

  .dark-step-1 {
    @extend %instruction-dark-theme-arrow-left;
    &:before {
      @apply top-1/2 transform -translate-y-1/2;
      left: -39px;
    }
  }

  .origin-step-2 {
    @extend %instruction-origin-theme-arrow-right;
    &:before {
      @apply top-1/2 transform -translate-y-1/2;
      right: -39px;
    }
  }

  .dark-step-2 {
    @extend %instruction-dark-theme-arrow-right;
    &:before {
      @apply top-1/2 transform -translate-y-1/2;
      right: -39px;
    }
  }

  .origin-step-3 {
    @extend %instruction-origin-theme-arrow-right;
    &:before {
      right: -39px;
      top: 9px;
    }
  }

  .dark-step-3 {
    @extend %instruction-dark-theme-arrow-right;
    &:before {
      right: -39px;
      top: 9px;
    }
  }

  .dark-step-4 {
    @extend %instruction-dark-theme-arrow-left;
    &:before {
      @apply top-1/2 transform -translate-y-1/2;
      left: -39px;
      top: 33px;
    }
  }

  .origin-step-4 {
    @extend %instruction-origin-theme-arrow-left;
    &:before {
      @apply top-1/2 transform -translate-y-1/2;
      left: -39px;
      top: 33px;
    }
  }
</style>
